// src/lib/fastDl.js
// Multi-Range com progress e StreamSaver opcional (zero RAM em arquivos grandes).
export async function parallelRangeDownload({
  url,
  filename = 'download',
  size,                 // total em bytes; se omitido, detecta via Range 0-0
  parts,                // nº conexões; auto (4..8)
  chunkSize,            // tamanho por parte; auto (16 MiB desktop, 8 MiB mobile)
  onProgress,           // (loaded, total, bps)
  retries = 2,
  timeoutMs = 60_000,
  useStreamSaver = true
}) {
  // 1) Descobrir tamanho se necessário
  if (!size) {
    const r0 = await fetch(url, { headers: { Range: 'bytes=0-0' } });
    if (r0.status !== 206) throw new Error('Servidor não suportou Range 206 no probe');
    const cr = r0.headers.get('Content-Range') || '';
    size = parseInt(cr.split('/')[1], 10);
  }

  // 2) Parâmetros dinâmicos
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);
  const effChunk = chunkSize ?? (isMobile ? 8 * 1024 * 1024 : 16 * 1024 * 1024);
  const effParts = parts ?? Math.min(8, Math.max(4, Math.ceil(size / (32 * 1024 * 1024))));

  // 3) Medidor de velocidade
  let loadedBytes = 0, lastT = Date.now(), lastB = 0, bpsVal = 0;
  const tick = (n) => {
    loadedBytes += n;
    const now = Date.now(), dt = (now - lastT) / 1000;
    if (dt >= 0.75) { bpsVal = (loadedBytes - lastB) / dt; lastB = loadedBytes; lastT = now; }
    onProgress?.(loadedBytes, size, bpsVal);
  };

  // 4) Saída: StreamSaver OU Blob
  const hasStreamSaver = !!(useStreamSaver && globalThis.streamSaver?.createWriteStream);
  let writer = null, blobParts = [];
  const writeChunk = async (u8) => { writer ? await writer.write(u8) : blobParts.push(u8); };

  if (hasStreamSaver) {
    const fileStream = globalThis.streamSaver.createWriteStream(filename, { size });
    writer = fileStream.getWriter();
  }

  // 5) helpers
  const fetchWithTimeout = (input, init={}) => {
    const ctrl = new AbortController(); const id = setTimeout(()=>ctrl.abort(), timeoutMs);
    return fetch(input, { ...init, signal: ctrl.signal }).finally(()=>clearTimeout(id));
  };

  const getRange = async (start, end) => {
    let lastErr;
    for (let a=0; a<=retries; a++) {
      try {
        const resp = await fetchWithTimeout(url, { headers: { Range: `bytes=${start}-${end}` } });
        if (resp.status !== 206) throw new Error(`Status ${resp.status}`);
        const reader = resp.body?.getReader?.();
        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            await writeChunk(value); tick(value.byteLength);
          }
        } else {
          const ab = await resp.arrayBuffer(); await writeChunk(new Uint8Array(ab)); tick(ab.byteLength);
        }
        return;
      } catch (e) { lastErr = e; }
    }
    throw lastErr;
  };

  // 6) baixar em lotes de effParts na ordem crescente
  let next = 0;
  while (next < size) {
    const ranges = [];
    for (let i=0; i<effParts && next<size; i++) {
      const s = next, e = Math.min(s + effChunk - 1, size - 1);
      ranges.push([s,e]); next = e + 1;
    }
    await Promise.all(ranges.map(([s,e]) => getRange(s,e)));
  }

  // 7) finalizar
  if (writer) {
    await writer.close();
  } else {
    const blob = new Blob(blobParts, { type: 'application/octet-stream' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = filename; a.click();
    setTimeout(()=>URL.revokeObjectURL(a.href), 10_000);
  }
}
